import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Icon } from '@iconify/react';
import { TextsContext } from '../../../context/textsContext';
import { useCountryOptions } from '../../../hooks/myFavoritePlan/useCountryOptions';
import style from '../../../scss/dashboardFilters.module.scss';

const DashboardFilters = ({ filters, setFilter, resetFilters, hasActiveFilters }) => {
    const { texts } = useContext(TextsContext);
    const { options: countryOptions } = useCountryOptions();

    // Time window the plan was created within (maps 1:1 to the API `createdWithin` enum).
    const createdWithinOptions = useMemo(() => [
        { value: 'lastMonth', label: texts?.dashboard_filter_last_month ?? 'Last month' },
        { value: 'last3Months', label: texts?.dashboard_filter_last_3_months ?? 'Last 3 months' },
        { value: 'last6Months', label: texts?.dashboard_filter_last_6_months ?? 'Last 6 months' },
        { value: 'lastYear', label: texts?.dashboard_filter_last_year ?? 'Last year' },
        { value: 'all', label: texts?.dashboard_filter_all_time ?? 'All time' },
    ], [texts]);

    // Compact sort labels (maps 1:1 to the API `orderBy` enum) to keep the row tight.
    const orderByOptions = useMemo(() => [
        { value: 'createdDate:desc', label: texts?.dashboard_sort_created_desc ?? 'Created ↓' },
        { value: 'createdDate:asc', label: texts?.dashboard_sort_created_asc ?? 'Created ↑' },
        { value: 'lastUpdatedAt:desc', label: texts?.dashboard_sort_updated_desc ?? 'Updated ↓' },
        { value: 'lastUpdatedAt:asc', label: texts?.dashboard_sort_updated_asc ?? 'Updated ↑' },
    ], [texts]);

    // Local, debounced search to avoid spamming the URL/history on every keystroke.
    const [searchValue, setSearchValue] = useState(filters.search);
    const debounceRef = useRef(null);

    useEffect(() => {
        setSearchValue(filters.search);
    }, [filters.search]);

    const onSearchChange = (e) => {
        const value = e.target.value;
        setSearchValue(value);
        if (debounceRef.current) clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => setFilter('search', value), 500);
    };

    useEffect(() => () => debounceRef.current && clearTimeout(debounceRef.current), []);

    const clearSearch = () => {
        if (debounceRef.current) clearTimeout(debounceRef.current);
        setSearchValue('');
        setFilter('search', '');
    };

    return (
        <div className={style.filters}>
            {/* Search */}
            <div className={style.searchBox}>
                <Icon icon="mdi:magnify" className={style.searchIcon} />
                <span className={style.searchDivider} />
                <input
                    type="text"
                    className={style.searchInput}
                    placeholder={texts?.dashboard_search_placeholder ?? 'Search...'}
                    value={searchValue}
                    onChange={onSearchChange}
                />
                {searchValue && (
                    <button
                        type="button"
                        className={style.searchClear}
                        onClick={clearSearch}
                        aria-label={texts?.dashboard_search_clear ?? 'Clear search'}
                    >
                        <Icon icon="mdi:close" />
                    </button>
                )}
            </div>

            {/* Filters */}
            <div className={style.filterBox}>
                <span className={style.filterByGroup}>
                    <Icon icon="mdi:filter-variant" className={style.funnelIcon} />
                    <span className={style.filterByLabel}>{texts?.dashboard_filter_by ?? 'Filter by:'}</span>
                </span>

                <div className={`${style.filterControl} ${style.selectWrap}`}>
                    <select
                        className={`${style.select} ${!filters.country ? style.selectPlaceholder : ''}`}
                        value={filters.country}
                        onChange={(e) => setFilter('country', e.target.value)}
                    >
                        <option value="">{texts?.dashboard_filter_country ?? 'By Country'}</option>
                        {countryOptions.map((country) => (
                            <option key={country.value} value={country.value}>{country.label}</option>
                        ))}
                    </select>
                    <Icon icon="mdi:chevron-down" className={style.fieldArrow} />
                </div>

                <span className={style.controlDivider} />

                <div className={`${style.filterControl} ${style.selectWrap}`}>
                    <select
                        className={`${style.select} ${!filters.createdWithin ? style.selectPlaceholder : ''}`}
                        value={filters.createdWithin}
                        onChange={(e) => setFilter('createdWithin', e.target.value)}
                    >
                        <option value="">{texts?.dashboard_filter_created_within ?? 'By Date'}</option>
                        {createdWithinOptions.map((option) => (
                            <option key={option.value} value={option.value}>{option.label}</option>
                        ))}
                    </select>
                    <Icon icon="mdi:chevron-down" className={style.fieldArrow} />
                </div>

                <span className={style.controlDivider} />

                <div className={`${style.filterControl} ${style.selectWrap}`}>
                    <select
                        className={`${style.select} ${!filters.orderBy ? style.selectPlaceholder : ''}`}
                        value={filters.orderBy}
                        onChange={(e) => setFilter('orderBy', e.target.value)}
                    >
                        <option value="">{texts?.dashboard_sort_placeholder ?? 'Sort'}</option>
                        {orderByOptions.map((option) => (
                            <option key={option.value} value={option.value}>{option.label}</option>
                        ))}
                    </select>
                    <Icon icon="mdi:chevron-down" className={style.fieldArrow} />
                </div>

                <span className={style.controlDivider} />

                <button
                    type="button"
                    className={`${style.resetBtn} ${hasActiveFilters ? style.resetActive : ''}`}
                    onClick={resetFilters}
                >
                    <Icon icon="mdi:backup-restore" className={style.resetIcon} />
                    {texts?.dashboard_reset_filter ?? 'Reset Filter'}
                </button>
            </div>
        </div>
    );
};

export default DashboardFilters;
